import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from "@angular/core";
import { ApiService } from "app/services/api.service";
import { Router } from "@angular/router";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { StaffModel, IdentityUser } from "app/models/staff.model";
import { ValidateUsernameAsync } from "app/services/validators.service";
import { AlarmRouteModel, Route } from "../../../models/alarmroutes.model";
import { ReceiverTeam } from "../../../models/receiver-team";
import { HelperService } from "../../../services/helper.service";
import { UnlocDoorTypes } from "app/models/unloc.model";
import { Subject, forkJoin, of } from "rxjs";
import { catchError, throttleTime } from "rxjs/operators";
import { LockTypesPipe, MomentPipe } from "../../../pipes/pipes";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { CommonModule } from "@angular/common";
import { v4 as uuidv4 } from "uuid";
import {
  passwordValidator,
  passwordsMatchValidator,
} from "app/shared/password-validator.shared";

export interface StaffUpdateEvent {
  staff: StaffModel;
  updateUsername: boolean;
}

@Component({
    selector: "staff-info",
    templateUrl: "./staff-info.component.html",
    styleUrls: ["./staff-info.component.css"],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        MatTooltipModule,
        TranslateModule,
        MomentPipe,
        LockTypesPipe
    ]
})
export class StaffInfoComponent implements OnChanges, OnDestroy, OnInit {
  constructor(
    private api: ApiService,
    private router: Router,
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private usernameValidator: ValidateUsernameAsync,
    private helper: HelperService
  ) { }

  @Input() staff: StaffModel;
  @Input() locations: any;
  @Output() closeStaffCardEvent = new EventEmitter<any>();
  @Output() staffUpdateEvent = new EventEmitter<StaffUpdateEvent>();
  @Output() staffInsertEvent = new EventEmitter<StaffModel>();
  @Output() staffDeleteEvent = new EventEmitter<string>();

  initialDataLoaded: boolean;
  alarmRouteLocations: AlarmRouteModel[] = [];
  availableAlarmRouteLocations: AlarmRouteModel[] = [];
  receiverTeamLocations: ReceiverTeamLocation[];
  availableReceiverTeamLocations: ReceiverTeamLocation[];
  user: any;
  editMode: boolean;
  staffEditForm: UntypedFormGroup;
  updateSuccess: boolean;
  updateError: boolean;
  updateErrorMessage: string | null = null;
  deleteConfirmation: boolean;
  enhancedSecurityFeature: boolean;
  identityUsers: IdentityUser[] = [];
  nonFederatedUsers: IdentityUser[] = [];
  identityUserSearchTerm = "";
  staffLocationList: string[];

  alarmRouteDropDownOpen: boolean = false;
  receiverTeamDropDownOpen: boolean = false;
  lockTypes = UnlocDoorTypes;
  //Select lock types by default which do not require special permissions
  selectedLockTypes: any;
  showUnlockSection = true;

  formSubmitSubject = new Subject<void>();
  isSwedishCustomer = false;

  private getPasswordValidationMessage(err: any): string | null {
    if (err?.error?.code) {
      return this.translate.instant(err.error.code);
    }
    return err?.error?.errors?.[0]
      ? this.translate.instant(err.error.errors[0])
      : null;
  }

  createStaffEditForm(): void {
    const enhancedPasswordValidatorRegex = new RegExp(
      "^(?=.*\\S)[\\s\\S]{12,}$"
    );
    const gsmNumberRegex = new RegExp(
      "^\\+(9[976]\\d|8[987530]\\d|6[987]\\d|5[90]\\d|42\\d|3[875]\\d|2[98654321]\\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1)\\d{1,14}$"
    );

    this.staffEditForm = this.fb.group({
      firstName: this.staff.firstName,
      familyName: this.staff.familyName,
      username: [this.staff.username, { updateOn: "blur" }],
      location: [this.staff.location, Validators.required],
      accessLocations: [this.staffLocationList],
      alarmRoutes: [
        {
          value: this.staff.alarmRoutes,
          disabled: !this.staff.location && this.locations.length !== 1,
        },
      ],
      receiverTeams: [
        {
          value: this.staff.teams,
          disabled: !this.staff.location && this.locations.length !== 1,
        },
      ],
      gsmNumber: [
        this.staff.gsmNumber,
        { validators: Validators.pattern(gsmNumberRegex), updateOn: "blur" },
      ],
      email: [this.staff.email, Validators.email],
      language: [this.staff.language, Validators.required],
      usingLevel: [this.staff.usingLevel, Validators.required],
      lockType: [this.selectedLockTypes],
      password: [
        "",
        {
          validators: this.enhancedSecurityFeature
            ? Validators.pattern(enhancedPasswordValidatorRegex)
            : passwordValidator(),
          updateOn: "blur",
        },
      ],
      confirmPassword: [
        "",
        {
          validators: this.enhancedSecurityFeature
            ? Validators.pattern(enhancedPasswordValidatorRegex)
            : passwordValidator(),
          updateOn: "blur",
        },
      ],
      locked: this.staff.locked,
      identityUser: this.staff.identityUsername,
      temporaryCodeAccess: [this.staff.temporaryCodeAccess || false],
    }, { validators: passwordsMatchValidator() });

    //Add required validators to password and confirm password fields if user is creating new staff and enhanced security feature is enabled
    if (!this.staff.id && this.enhancedSecurityFeature) {
      this.staffEditForm.controls["password"].setValidators([
        Validators.required,
        Validators.pattern(enhancedPasswordValidatorRegex)
      ]);

      this.staffEditForm.controls["password"].updateValueAndValidity();

      this.staffEditForm.controls["confirmPassword"].setValidators([
        Validators.required,
        Validators.pattern(enhancedPasswordValidatorRegex)
      ]);
      this.staffEditForm.controls["confirmPassword"].updateValueAndValidity();
    }

    if (this.staff.identityUsername) {
      //do not set validators if staff was integrated
    } else {
      this.staffEditForm.controls["username"].setValidators([
        Validators.required,
        Validators.pattern(new RegExp("^\\S+$")),
      ]);
      this.staffEditForm.controls["username"].setAsyncValidators(
        this.usernameValidator.validate.bind(this.usernameValidator)
      );
      this.staffEditForm.controls["username"].updateValueAndValidity();
    }

    if (this.locations.length === 1) {
      this.staffEditForm.get("location").setValue(this.locations[0]);
      this.staffEditForm.get("location").disable();
    }

    this.staffEditForm.get("location").valueChanges.subscribe((_) => {
      this.staffEditForm.get("alarmRoutes").enable();
      this.staffEditForm.get("alarmRoutes").reset([]);
      this.staffEditForm.get("receiverTeams").enable();
      this.staffEditForm.get("receiverTeams").reset([]);
      this.parseRoutes();
      this.parseReceiverTeams();
    });

    // Subscribe to lockType changes to update temporary code access state
    this.staffEditForm.get("lockType").valueChanges.subscribe((_) => {
      this.updateTemporaryCodeAccessState();
    });

    // Subscribe to usingLevel changes to update temporary code access state
    this.staffEditForm.get("usingLevel").valueChanges.subscribe((_) => {
      this.updateTemporaryCodeAccessState();
    });

    // Initial check for temporary code access state
    this.updateTemporaryCodeAccessState();
  }

  unlockStaff() {
    this.staffEditForm.controls.locked.patchValue(false);
    this.submitStaffEdit();
  }

  compareByRouteId(o1: any, o2: any): boolean {
    return o1 && o2 && o1.id === o2.id;
  }

  compareByGroupId(o1: any, o2: any): boolean {
    return o1 && o2 && o1.groupId === o2.groupId;
  }

  closeStaffCard(): void {
    this.closeStaffCardEvent.emit(this.staff);
  }

  isFeatureEnabled(feature: string): boolean {
    const features: [string] = JSON.parse(localStorage.getItem("features"));
    if (features) {
      return features.includes(feature);
    }
    return false;
  }

  filterIdentityUsers(users: IdentityUser[]): IdentityUser[] {
    const searchTerm = this.identityUserSearchTerm.trim().toLowerCase();
    if (!searchTerm) {
      return users;
    }

    return users.filter((user) =>
      [user.firstname, user.familyName, user.username, user.email]
        .some((value) => value?.toLowerCase().includes(searchTerm))
    );
  }

  getLanguageString(lang: string) {
    switch (lang) {
      case "FI":
        return this.translate.instant("FINNISH");
      case "EN":
        return this.translate.instant("ENGLISH");
      case "ES":
        return this.translate.instant("SPANISH");
      case "SE":
        return this.translate.instant("SWEDISH");
      case "DE":
        return this.translate.instant("GERMAN");
      case "FR":
        return this.translate.instant("FRENCH");
      default:
        return this.translate.instant("UNKNOWN_LANGUAGE");
    }
  }

  getUsingLevelString(usingLevel: number): string {
    switch (usingLevel) {
      case 101:
        return this.translate.instant("STAFF_CUSTOMER_ADMIN");
      case 100:
        return this.translate.instant("STAFF_MAIN_USER");
      case 50:
        return this.translate.instant("STAFF_USER");
      case 10:
        return this.translate.instant("EveronMobileApp");
      case 0:
        return this.translate.instant("STAFF_READONLY");
      default:
        "";
    }
  }

  featureEnabled(feature: string): boolean {
    const features = JSON.parse(localStorage.getItem("features"));
    if (features) {
      const index = features.indexOf(feature);
      if (index !== -1) {
        return true;
      }
    }
    return false;
  }

  hasMedicineCabinetAccess(): boolean {
    const selectedLockTypes = this.staffEditForm.get('lockType')?.value;
    return selectedLockTypes?.some((lockType: string) => ['12', '13', '14'].includes(lockType)) || false;
  }

  shouldShowTemporaryCodeAccess(): boolean {
    if (!this.isFeatureEnabled('DigitalKey')) {
      return false;
    }

    // If staff being viewed is superuser (>=101) AND viewer is admin/superuser (>=101): always show
    if (this.staff.usingLevel >= 101 && this.user.roleLevel >= 101) {
      return true;
    }

    // If staff has medicine cabinet access: show
    if (this.hasMedicineCabinetAccess()) {
      return true;
    }

    return false;
  }

  isTemporaryCodeAccessDisabled(): boolean {
    // Only Everon admin or Superuser (roleLevel >= 101) can edit
    return this.user.roleLevel < 101;
  }

  updateTemporaryCodeAccessState(): void {
    if (this.staffEditForm) {
      const temporaryCodeControl = this.staffEditForm.get('temporaryCodeAccess');
      if (!this.shouldShowTemporaryCodeAccess() && temporaryCodeControl) {
        // disable checkbox
        temporaryCodeControl.disable();
      } else if (temporaryCodeControl) {
        if (this.isTemporaryCodeAccessDisabled()) {
          // If checkbox is visible but user can't edit it, disable it but keep current value
          temporaryCodeControl.disable();
        } else {
          // If checkbox is visible and user can edit it, enable it
          temporaryCodeControl.enable();
        }
      }
    }
  }

  staffEditMode(): void {
    //If staff does not have ID user is creating new staff so close the whole card
    if (!this.staff.id) {
      this.closeStaffCard();
      return;
    }
    //Hide all information and error messages
    this.updateError = false;
    this.updateSuccess = false;

    //If edit mode is not enabled create new form and enable edit mode.
    if (!this.editMode) {
      this.createStaffEditForm();
    }
    this.toggleEditMode(!this.editMode);
  }

  async createStaffInformation(data: any): Promise<any> {
    return await new Promise<any>((resolve, reject) =>
      this.api.addNewStaff(data).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => reject(err)
      )
    );
  }

  async updateStaffInformation(data: any, newUuid: string): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      try {
        // Include current alarm routes and receiver teams to remove user from them, in case user is relocated from top level to a location
        data.receiverTeamsToRemove = this.mapReceiverTeamsIdsToRemove(data);
        data.alarmRoutesToRemove = this.mapAlarmRoutesIdsToRemove(data);

        // Remove fields in order to avoid errors in backend
        delete data.alarmRoutes;
        delete data.receiverTeams;

        this.api.saveStaffInfo(data, newUuid).subscribe(
          (res) => {
            resolve(res);
          },
          (err) => reject(err)
        );
      } catch (error) {
        console.error(error);
        reject();
      }
    });
  }

  async updateStaffAlarmRoutes(
    personnelId: string,
    alarmRoutes: Route[],
    newUuid: string = ""
  ): Promise<any> {
    let alarmRouteIds: string[] = [];
    if (alarmRoutes) {
      alarmRouteIds = alarmRoutes.map((alarmRoute) => alarmRoute.id);
    }

    await new Promise<any>((resolve, reject) =>
      this.api
        .updatePersonnelAlarmRoutes(personnelId, alarmRouteIds, newUuid)
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => reject()
        )
    );
  }

  async updateStaffReceiverTeams(
    personnelId: string,
    receiverTeams: ReceiverTeam[],
    newUuid: string = ""
  ): Promise<any> {
    const receiverTeamIds: { groups: string[] } = { groups: [] };
    if (receiverTeams) {
      receiverTeams.forEach((team) =>
        receiverTeamIds.groups.push(team.groupId)
      );
    }

    await new Promise<any>((resolve, reject) =>
      this.api
        .updatePersonnelReceiverTeams(personnelId, receiverTeamIds, newUuid)
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => reject()
        )
    );
  }

  async updateAuthenticatedUser(newUuid: string): Promise<any> {
    const selectedValue = this.staffEditForm.value.identityUser;
    const selectedNonFed = this.nonFederatedUsers.find(u => u.staffId === selectedValue);

    if (selectedNonFed) {
      const data = { staffId: this.staff.id, staffIdToLink: selectedNonFed.staffId };
      await new Promise<any>((resolve, reject) =>
        this.api.linkNonFederatedUser(data, newUuid).subscribe(
          (res) => {
            this.staff.identityUsername = selectedNonFed.staffId;
            resolve(res);
          },
          (err) => reject(err)
        )
      );
    } else {
      const data = { username: selectedValue, personnelId: this.staff.id };
      await new Promise<any>((resolve, reject) =>
        this.api.updateAuthenticatedUser(data, newUuid).subscribe(
          (res) => {
            this.staff.identityUsername = selectedValue;
            resolve(res);
          },
          (err) => reject(err)
        )
      );
    }
  }

  async updateLocationAccess(newUuid: string): Promise<any> {
    const data = {
      personnelId: this.staff.id,
      locations: this.staffEditForm.value.accessLocations,
    };
    await new Promise<any>((resolve, reject) =>
      this.api.saveStaffLocationList(data, newUuid).subscribe(
        (res) => resolve(res),
        (err) => reject(err)
      )
    );
  }

  async updateLockOpeningRights(newUuid: string = ""): Promise<any> {
    const selectedLockTypes = this.staffEditForm.controls.lockType.value;
    const allLockTypes = UnlocDoorTypes;
    const data: any = [];
    // Check what lock types are selected and what are disabled
    allLockTypes.forEach((l) => {
      const lockType = {
        lockedDoorTypeId: parseInt(l.value),
        requirePermission: l.requirePermission,
        selected: false,
      };
      if (selectedLockTypes.includes(l.value)) {
        lockType.selected = true;
      }
      data.push(lockType);
    });

    await new Promise<any>((resolve) =>
      this.api
        .updateStaffLockOpeningRights(this.staff.id, data, newUuid)
        .subscribe((res) => {
          this.selectedLockTypes = selectedLockTypes;
          resolve(res);
        })
    );
  }

  async updateStaff(data: any): Promise<void> {
    const newUuid = uuidv4();

    if (this.staffEditForm.controls.identityUser.dirty) {
      await this.updateAuthenticatedUser(newUuid);
    }
    if (this.staffEditForm.controls.accessLocations.dirty) {
      await this.updateLocationAccess(newUuid);
    }
    if (this.staffEditForm.controls.alarmRoutes.dirty) {
      await this.updateStaffAlarmRoutes(data.id, data.alarmRoutes, newUuid);
    }
    if (this.staffEditForm.controls.receiverTeams.dirty) {
      await this.updateStaffReceiverTeams(data.id, data.receiverTeams, newUuid);
    }

    if (this.staffEditForm.controls.lockType.dirty) {
      await this.updateLockOpeningRights(newUuid);
    }

    await this.updateStaffInformation(data, newUuid);
  }

  async saveNewStaff(data: any): Promise<any> {
    const newStaffResponse = await this.createStaffInformation(data);
    await this.updateStaffAlarmRoutes(newStaffResponse.id, data.alarmRoutes);
    await this.updateStaffReceiverTeams(
      newStaffResponse.id,
      data.receiverTeams
    );
    return newStaffResponse;
  }

  submitStaffEdit(): void {
    this.updateError = false;
    this.updateErrorMessage = null;
    this.updateSuccess = false;
    this.showUnlockSection = false;

    //Make deep copy of form data
    const data = JSON.parse(JSON.stringify(this.staffEditForm.value));
    const authenticatedUserChanged = this.staffEditForm.controls.identityUser.dirty;

    // Store original temporaryCodeAccess value to check for modification
    const originalTemporaryCodeAccess = this.staff.temporaryCodeAccess || false;

    // If temporary code access checkbox is hidden, set it to false when saving
    if (!this.shouldShowTemporaryCodeAccess()) {
      data.temporaryCodeAccess = false;
    }

    // Check if temporaryCodeAccess was modified (changed from original value)
    const currentTemporaryCodeAccess = data.temporaryCodeAccess || false;
    data.temporaryCodeAccessModified = originalTemporaryCodeAccess !== currentTemporaryCodeAccess;

    //this.staffEditForm.value.username = "";

    data.id = this.staff.id;
    data.locationId = this.staffEditForm.getRawValue().location.id;

    if (this.staffEditForm.hasError("passwordsMismatch")) {
      return;
    }

    //When editing an existing staff member, only send a password if the user
    //actually entered one. Otherwise an empty password would be submitted and
    //rejected by the password policy, even though the password isn't changing.
    if (data.id && !data.password) {
      delete data.password;
      delete data.confirmPassword;
    }
    //If data already has ID user is editing staff details. Use right API for that and otherwise submit new staff
    if (data.id) {
      this.updateStaff(data).then(
        (_) => {
          //Get updated information from server if save was successfull
          this.api.getStaffInfo(data.id).subscribe((res) => {
            this.staff = res;
            this.updateSuccess = true;
            this.staffUpdateEvent.emit({
              staff: this.staff,
              updateUsername: !authenticatedUserChanged,
            });
            //Find correct location with location ID
            this.staff.location = this.locations.find((obj) => {
              return obj.id === this.staff.locationId;
            });
          });
          this.toggleEditMode(false);
        },
        (err) => {
          this.updateError = true;
          this.updateErrorMessage = this.getPasswordValidationMessage(err);
          //Set username back into form in case of error in submit
          this.staffEditForm.value.username = data.username;
        }
      );
    } else {
      this.saveNewStaff(data).then(
        (response) => {
          //Update lock opening rights after save so we have staff ID
          if (this.staffEditForm.controls.lockType.dirty) {
            this.staff.id = response.id;
            this.updateLockOpeningRights();
          }

          //Get updated information from server if save was successfull
          this.api.getStaffInfo(response.id).subscribe((res) => {
            this.toggleEditMode(false);
            this.staffInsertEvent.emit(res);
          });
        },
        (err) => {
          this.updateError = true;
          this.updateErrorMessage = this.getPasswordValidationMessage(err);
        }
      );
    }
  }

  //Check if typed username is the same as the old one and mark field as pristine so it passes validation
  accountNameBlurMethod(): void {
    if (this.staffEditForm.value.username === this.staff.username) {
      this.staffEditForm.controls.username.markAsPristine();
      this.staffEditForm.controls.username.updateValueAndValidity();
    }
  }

  deleteStaff(): void {
    this.updateError = false;
    this.updateSuccess = false;
    this.api.deleteStaff(this.staff.id).subscribe(
      (res) => {
        this.staffDeleteEvent.emit(this.staff.id);
      },
      (err) => {
        this.updateError = true;
      }
    );
  }

  parseRoutes(): void {
    if (this.staffEditForm && this.staffEditForm.getRawValue().location) {
      const formLocationId = this.staffEditForm.getRawValue().location.id;
      if (formLocationId) {
        if (formLocationId.startsWith("C0")) {
          if (this.alarmRouteLocations) {
            this.availableAlarmRouteLocations = JSON.parse(
              JSON.stringify(this.alarmRouteLocations)
            );
          }
        } else {
          this.availableAlarmRouteLocations = [
            this.alarmRouteLocations.find(
              (location) => location.locationId === formLocationId
            ),
          ];
        }
      }
    }
  }

  parseReceiverTeams(): void {
    if (this.staffEditForm && this.staffEditForm.getRawValue().location) {
      if (this.receiverTeamLocations) {
        const formLocationId = this.staffEditForm.getRawValue().location.id;
        if (formLocationId) {
          if (formLocationId.startsWith("C0")) {
            this.availableReceiverTeamLocations = JSON.parse(
              JSON.stringify(this.receiverTeamLocations)
            );
          } else {
            this.availableReceiverTeamLocations = [
              this.receiverTeamLocations.find(
                (location) => location.locationId === formLocationId
              ),
            ];
          }
        }
      }
    }
  }

  async getAlarmRoutes(): Promise<void> {
    await new Promise<void>((resolve) =>
      this.api.getAlarmRoutes().subscribe((res) => {
        if (res) {
          this.alarmRouteLocations = res;
        }
        resolve();
      })
    );
  }

  async getReceiverTeams(): Promise<void> {
    const convertReceiverTeamsIntoLocations = (res: ReceiverTeam[]) => {
      if (!res) {
        return;
      }
      const receiverGroupRawLocations = this.helper.groupBy(
        res,
        (receiverTeam) => receiverTeam.locationId
      );

      this.receiverTeamLocations = receiverGroupRawLocations.map((teams) => {
        if (!teams[0].locationName) {
          return {
            locationName: teams[0].customerName,
            locationId: teams[0].locationId,
            receiverTeams: teams,
          };
        }
        return {
          locationName: teams[0].locationName,
          locationId: teams[0].locationId,
          receiverTeams: teams,
        };
      });
    };

    await new Promise<void>((resolve) =>
      this.api.getReceiverTeams().subscribe((res) => {
        convertReceiverTeamsIntoLocations(res);
        resolve();
      })
    );
  }

  async getIdentities(): Promise<void> {
    await new Promise<void>((resolve) =>
      forkJoin([
        this.api.getIdentityList().pipe(catchError(() => of([]))),
        this.api.getCompanyGroupNonFedStaff().pipe(catchError(() => of([])))
      ]).subscribe(([identities, nonFederatedRes]: [IdentityUser[], any]) => {
        this.identityUsers = identities;
        this.nonFederatedUsers = (nonFederatedRes || [])
          .filter((u: any) => u.username)
          .map((u: any) => ({ username: u.username, firstname: u.firstname, familyName: u.familyName, email: u.email, staffId: u.staffId }));
        resolve();
      })
    );
  }

  async getLockOpeningRights(): Promise<void> {
    if (!this.staff.id) {
      this.selectedLockTypes = UnlocDoorTypes.map((x) =>
        x.requirePermission === false ? x.value : undefined
      );
      return;
    }

    await new Promise<void>((resolve) =>
      this.api.getStaffLockOpeningRights(this.staff.id).subscribe((res) => {
        this.selectedLockTypes = res.map((x) => {
          if (x.permitted) {
            return x.lockedDoorTypeId.toString();
          }
        });
        resolve();
      })
    );
  }

  async getStaffLocationList(): Promise<void> {
    await new Promise<void>((resolve) => {
      // Skip fetching the location access list for the new user
      if (!this.staff.id) resolve();

      this.api.getStaffLocationList(this.staff.id).subscribe((res) => {
        if (res) {
          this.staffLocationList = res;
        }
        resolve();
      })
    });
  }

  async fetchInitialData(): Promise<void> {
    await this.getReceiverTeams();
    await this.getAlarmRoutes();
    if (
      this.isFeatureEnabled("StrongAuthentication") && this.user?.roleLevel >= 100
    ) {
      await this.getIdentities();
    }
    if (this.user?.roleLevel >= 100) {
      await this.getStaffLocationList();
    }
  }

  openAlarmRoutePage(routeId: string): void {
    this.router.navigate(["route/" + routeId]);
  }

  openReceiverTeamsPage(teamId: string): void {
    this.router.navigate(["teams/" + teamId]);
  }

  //Toggle editMode, If edit mode is true fetch AlarmRoutes and ReceiverTeams
  toggleEditMode(editMode: boolean) {
    this.editMode = editMode;
    if (editMode) {
      this.initialDataLoaded = false;
      this.fetchInitialData().then((_) => {
        this.createStaffEditForm();
        this.parseRoutes();
        this.parseReceiverTeams();
        this.initialDataLoaded = true;
      });
    }
  }

  mapAlarmRoutesIdsToRemove(data: any): string[] {
    return this.staff?.alarmRoutes
      ?.map((route) => route.id)
      .filter(
        (routeId) =>
          !data.alarmRoutes.map((route: Route) => route.id).includes(routeId)
      ); // Do not include routes to the remove list that are about to be reassigned
  }

  mapReceiverTeamsIdsToRemove(data: any): string[] {
    return this.staff?.teams
      ?.map((team) => team.groupId)
      .filter(
        (teamId) =>
          !data.receiverTeams
            .map((team: ReceiverTeam) => team.groupId)
            .includes(teamId)
      );
  }

  async ngOnInit(): Promise<void> {
    const customerInfo = this.helper.getCustomerInfo();
    this.isSwedishCustomer = customerInfo?.country === 'SE';
    if (this.isFeatureEnabled("DigitalKey")) {
      await this.getLockOpeningRights();
    }

    this.createStaffEditForm();
    // Initialize form submit subject with 3 second throttle time to prevent multiple submits
    this.formSubmitSubject.pipe(throttleTime(3000)).subscribe(() => {
      this.submitStaffEdit();
    });
  }


  ngOnChanges() {
    this.enhancedSecurityFeature = this.isFeatureEnabled("EnhancedSecurity");
    this.toggleEditMode(false);
    this.updateError = false;
    this.updateSuccess = false;

    // Find correct location from location list with location ID
    this.staff.location = this.locations.find((obj) => {
      return obj.id === this.staff.locationId;
    });

    this.user = JSON.parse(localStorage.getItem("user"));

    // If staff does not have ID user is creating new staff. Enable editing mode and initialize the edit form
    if (!this.staff.id) {
      this.toggleEditMode(true);
    }
  }

  ngOnDestroy(): void {
    this.formSubmitSubject.unsubscribe();
  }
}

interface ReceiverTeamLocation {
  locationName: string;
  locationId: string;
  receiverTeams: ReceiverTeam[];
}
